import React, { FC, useState } from 'react';

import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, TextField, createStyles, makeStyles,
} from '@material-ui/core';
import { useParams } from 'react-router';
import Page from '../page/Page';
import ProductList from './components/productlist/ProductList';
import ProductListItem from './components/productlist/ProductListItem';
import GET_ALL_PRODUCTS from '../../commons/gql/remote/GetAllProducts';
import GET_PRICESHEET from '../../commons/gql/remote/pricesheets/GetPricesheet';
import ADD_PRODUCT_TO_PRICESHEET from '../../commons/gql/remote/pricesheets/AddProductToPricesheet';
import REMOVE_PRODUCT_FROM_PRICESHEET from '../../commons/gql/remote/pricesheets/RemoveProductFromPricesheet';

const useStyles = makeStyles(() => createStyles({
  header: {
    paddingTop: '3rem',
    paddingBottom: '2rem',
  },
  filter: {
    paddingBottom: '1rem',
  },
}));

const getFilteredProducts = (allProducts: any[], pricesheetProducts?: any[], term?: string) => {
  const searchTerm = term === undefined ? '' : term;
  let result = allProducts;

  // Remove products from pricesheet products
  if (pricesheetProducts !== undefined && pricesheetProducts?.length > 0) {
    result = allProducts.filter((product) => (
      pricesheetProducts.findIndex((pp) => pp.product.id === product.id) === -1
    ));
  }

  // Remove products that don't meet the search term
  result = result.filter((product) => {
    const { brand, inRutaShortDescriptionSpanish } = product;
    return brand.toLowerCase().includes(searchTerm.toLowerCase())
      || inRutaShortDescriptionSpanish.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return result;
};

const EditPricesheetPage: FC = () => {
  const classes = useStyles();
  const { pricesheetId } = useParams();
  const [searchParam, setSearchParam] = useState<string>('');
  const { loading: productsLoading, data: productsData } = useQuery(GET_ALL_PRODUCTS);
  const { loading: pricesheetLoading, data: pricesheetData } = useQuery(GET_PRICESHEET, {
    variables: { pricesheetId },
  });
  const [addProductToPricesheet] = useMutation(ADD_PRODUCT_TO_PRICESHEET, {
    refetchQueries: [
      { query: GET_ALL_PRODUCTS },
      { query: GET_PRICESHEET, variables: { pricesheetId } },
    ],
  });
  const [removeProductFromPricesheet] = useMutation(REMOVE_PRODUCT_FROM_PRICESHEET, {
    refetchQueries: [
      { query: GET_ALL_PRODUCTS },
      { query: GET_PRICESHEET, variables: { pricesheetId } },
    ],
  });

  if (productsLoading || pricesheetLoading) return <div />;

  // Filter items
  const allProducts: any[] = productsData?.getProducts;
  const { baseMarkup, products: pricesheetProducts } = pricesheetData?.pricesheet;
  const listHeight = 400;
  const rowHeight = 100;
  const tableWidth = 600;
  const selectedProducts = pricesheetProducts.map((pp: any) => pp.product);
  const candidateProducts = getFilteredProducts(allProducts, pricesheetProducts, searchParam);

  return (
    <Page>
      <Typography
        variant="h3"
        className={classes.header}
      >
        Edit Pricesheet
      </Typography>
      <Box style={{ width: `${tableWidth}px`, height: `${listHeight}px`, display: 'inline-block' }}>
        <ProductList
          products={selectedProducts}
          listHeight={listHeight}
          rowHeight={rowHeight}
          renderer={(listProps, product) => {
            const price = pricesheetProducts.find(
              (pp: any) => pp.product.id === product.id,
            )?.price;
            return (
              <ProductListItem
                mode="remove"
                baseMarkup={baseMarkup}
                product={product}
                price={price}
                listProps={listProps}
                onClick={(productId) => {
                  removeProductFromPricesheet({
                    variables: {
                      pricesheetId,
                      productId,
                    },
                  });
                }}
              />
            );
          }}
        />
      </Box>
      <Box style={{ width: `${tableWidth}px`, height: `${listHeight}px`, display: 'inline-block' }}>
        <TextField className={classes.filter} onChange={(event) => { setSearchParam(event.target.value); }} placeholder="Filter products..." />
        <ProductList
          products={candidateProducts}
          listHeight={listHeight}
          rowHeight={rowHeight}
          renderer={(listProps, product) => (
            <ProductListItem
              mode="add"
              baseMarkup={baseMarkup}
              product={product}
              listProps={listProps}
              onClick={(productId, price) => {
                addProductToPricesheet({
                  variables: {
                    pricesheetId,
                    productId,
                    price,
                  },
                });
              }}
            />
          )}
        />
      </Box>
    </Page>
  );
};

export default EditPricesheetPage;
